import {
  ResourceTypeEnum,
  RoleTypeEnum,
  snakeCaseToDisplay,
} from "@superblocksteam/shared";

import { SHARE_MODAL_ROLE_SELECT_WIDTH } from "core/constants/share";

export { SHARE_MODAL_ROLE_SELECT_WIDTH };

type PageType = "entities" | "integrations";

export const getRoleSelectorWidth = (roleName: string | undefined) => {
  return roleName
    ? Math.min(roleName.length * 7 + 45, SHARE_MODAL_ROLE_SELECT_WIDTH)
    : SHARE_MODAL_ROLE_SELECT_WIDTH;
};

export enum RoleAndPermissionsPages {
  ORGANIZATION_ROLES = "organization-roles",
  RESOURCE_ROLES = "resource-roles",
  SETTINGS = "settings",
}

export const isValidRolesPageType = (
  pageType: string | undefined,
): pageType is PageType => {
  if (!pageType) return false;
  return Object.values(RoleAndPermissionsPages).includes(pageType as any);
};

// by default, we just take the snake_case name and make it Display case,
// but sometimes we want a nicer name
export const USER_FRIENDLY_RESOURCE_NAMES: Record<string, string> = {
  [ResourceTypeEnum.APPLICATIONS]: "Applications",
  [ResourceTypeEnum.SCHEDULED_JOBS]: "Scheduled jobs",
  [ResourceTypeEnum.WORKFLOWS]: "Workflows",
  [ResourceTypeEnum.INTEGRATIONS]: "Integrations",
  [ResourceTypeEnum.ORGANIZATION]: "Organization",
  [ResourceTypeEnum.REPOSITORIES]: "Repositories",
  [ResourceTypeEnum.POLICIES]: "Security",
  [ResourceTypeEnum.ORGANIZATION_USERS]: "Users",
  [ResourceTypeEnum.GROUPS_MEMBERS]: "Group Members",
  [ResourceTypeEnum.GROUPS]: "Groups",
  [ResourceTypeEnum.APPLICATIONS_APIS]: "Application APIs",
  [ResourceTypeEnum.APPLICATIONS_PAGES]: "Application pages",
  [ResourceTypeEnum.LOGS_STREAMS]: "Log Streams",
  [ResourceTypeEnum.LOGS]: "Logs",
  [ResourceTypeEnum.APP_DATABASES]: "App Databases",
  [ResourceTypeEnum.NPM_REGISTRY]: "Package Registry",
  [ResourceTypeEnum.AGENTS]: "Agents",
  [ResourceTypeEnum.PROFILES]: "Profiles",
  [ResourceTypeEnum.ROLES]: "Roles",
  [ResourceTypeEnum.SECRETS_STORES]: "Secrets Stores",
  [ResourceTypeEnum.ACCESS_TOKENS]: "Access Tokens",
  [ResourceTypeEnum.AI_INFERENCE]: "AI Inference",
  [ResourceTypeEnum.BILLING]: "Billing",
  [ResourceTypeEnum.FOLDERS]: "Folders",
  [ResourceTypeEnum.ORG_KNOWLEDGE]: "Organization Knowledge",
  [ResourceTypeEnum.INTEGRATIONS_DEFAULT_AI]: "Default AI Integration",
};

export const USER_FRIENDLY_ROLE_NAMES: Record<RoleTypeEnum, string> = {
  [RoleTypeEnum.ORGANIZATION]: "Organization",
  [RoleTypeEnum.APPLICATIONS]: "Application",
  [RoleTypeEnum.WORKFLOWS]: "Workflow",
  [RoleTypeEnum.SCHEDULED_JOBS]: "Scheduled Job",
  [RoleTypeEnum.INTEGRATIONS]: "Integration",
  // [RoleTypeEnum.GROUPS]: "Group",
  // [RoleTypeEnum.SECRETS_STORES]: "Secret Store",
  // [RoleTypeEnum.PROFILES]: "Profile",
};

export enum ROLES_SECTION_HEADER {
  DEVELOPMENT = "Development",
  GOVERNANCE = "Governance",
}
// in
export const ROLE_NAME_TO_SECTION_HEADER: Record<string, string> = {
  // development
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.APPLICATIONS]]:
    ROLES_SECTION_HEADER.DEVELOPMENT,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.FOLDERS]]:
    ROLES_SECTION_HEADER.DEVELOPMENT,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.INTEGRATIONS]]:
    ROLES_SECTION_HEADER.DEVELOPMENT,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.SCHEDULED_JOBS]]:
    ROLES_SECTION_HEADER.DEVELOPMENT,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.WORKFLOWS]]:
    ROLES_SECTION_HEADER.DEVELOPMENT,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.FOLDERS]]:
    ROLES_SECTION_HEADER.DEVELOPMENT,
  // governance
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.ACCESS_TOKENS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.AGENTS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.GROUPS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.LOGS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.ORGANIZATION]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.PROFILES]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.REPOSITORIES]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.POLICIES]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.ROLES]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.SECRETS_STORES]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.ACCESS_TOKENS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.BILLING]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.ORG_KNOWLEDGE]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  // Move from Other to Governance
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.ORGANIZATION_USERS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.GROUPS_MEMBERS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
  [USER_FRIENDLY_RESOURCE_NAMES[ResourceTypeEnum.LOGS_STREAMS]]:
    ROLES_SECTION_HEADER.GOVERNANCE,
};

export type UserInvitee = {
  name: string;
  email: string;
  type: "user";
  id: string;
};

type GroupInvitee = {
  name: string;
  type: "group";
  id: string;
  size?: number;
};

type ApplicationInvitee = {
  name: string;
  type: "application";
  id: string;
};

export type Invitee = UserInvitee | GroupInvitee | ApplicationInvitee;

export type InviteeToSend = {
  type: "user" | "group" | "application";
  id: string;
  name: string;
};

export type ResourceToRender = {
  id: string;
  name: string;
  type: ResourceTypeEnum;
  roles: string[];
  devEnvEnabled?: boolean;
};

export const convertResourceTypeToRoleType = (
  resourceType: ResourceTypeEnum,
): RoleTypeEnum | null => {
  const mapping: Record<ResourceTypeEnum, RoleTypeEnum | null> = {
    [ResourceTypeEnum.ACCESS_TOKENS]: null,
    [ResourceTypeEnum.AGENTS]: null,
    [ResourceTypeEnum.AI_INFERENCE]: RoleTypeEnum.INTEGRATIONS,
    [ResourceTypeEnum.APPLICATIONS]: RoleTypeEnum.APPLICATIONS,
    [ResourceTypeEnum.APPLICATIONS_APIS]: RoleTypeEnum.APPLICATIONS,
    [ResourceTypeEnum.APPLICATIONS_PAGES]: RoleTypeEnum.APPLICATIONS,
    [ResourceTypeEnum.BILLING]: null,
    [ResourceTypeEnum.FOLDERS]: null,
    [ResourceTypeEnum.GROUPS]: null,
    [ResourceTypeEnum.GROUPS_MEMBERS]: null,
    [ResourceTypeEnum.INTEGRATIONS]: RoleTypeEnum.INTEGRATIONS,
    // Group the org-default-AI permission with INTEGRATIONS so it appears under the
    // same role-type group in the permissions UI as the parent `INTEGRATIONS` resource
    // it scopes (and so this mapping matches `packages/ui/src/app/pages/Permissions`).
    [ResourceTypeEnum.INTEGRATIONS_DEFAULT_AI]: RoleTypeEnum.INTEGRATIONS,
    [ResourceTypeEnum.LOGS]: null,
    [ResourceTypeEnum.LOGS_STREAMS]: null,
    [ResourceTypeEnum.APP_DATABASES]: null,
    [ResourceTypeEnum.NPM_REGISTRY]: null,
    [ResourceTypeEnum.ORGANIZATION]: RoleTypeEnum.ORGANIZATION,
    [ResourceTypeEnum.ORGANIZATION_USERS]: RoleTypeEnum.ORGANIZATION,
    [ResourceTypeEnum.ORG_KNOWLEDGE]: null,
    [ResourceTypeEnum.PROFILES]: null,
    [ResourceTypeEnum.REPOSITORIES]: null,
    [ResourceTypeEnum.POLICIES]: null,
    [ResourceTypeEnum.ROLES]: null,
    [ResourceTypeEnum.SCHEDULED_JOBS]: RoleTypeEnum.SCHEDULED_JOBS,
    [ResourceTypeEnum.SECRETS_STORES]: null,
    [ResourceTypeEnum.WORKFLOWS]: RoleTypeEnum.WORKFLOWS,
  };

  return mapping[resourceType] ?? null;
};

export const getResourceTypeDisplayName = (
  type: ResourceTypeEnum,
  single = true,
) => {
  const name =
    USER_FRIENDLY_RESOURCE_NAMES[type] ??
    snakeCaseToDisplay(type.replaceAll(".", "_"));

  if (name[name.length - 1] === "s" && single) {
    return name.slice(0, -1);
  }
};

export const sortRoleOrder: Record<string, number> = {
  Owner: 0,
  Admin: 1,
  Developer: 2,
  "End-User": 3,
  Max: 4,
};
